/**
 * TransferCardParts — the context blocks of a branch-transfer card
 * (owner 2026-09-19). Logic lives in lib/transferCard.js; these only draw.
 *
 *   <WaitChip>          "Waiting 3 days" — silent under a day
 *   <WhoseMoveLine>     who has to act now, in one sentence
 *   <TransferContents>  units the warehouse counts in, total + per category
 *   <TransferTimeline>  dated, named steps for the path this transfer took
 *
 * TransferTimeline is shared by the list card and the View dialog so the two
 * can never tell different stories about the same transfer.
 */
import { AlertTriangle, Clock, Hourglass } from 'lucide-react';
import { formatDateTimeShort } from '../lib/dateFormat';
import { doorToDoorDays, unitSummary, waitAge } from '../lib/transferCard';

const MAX_CATEGORIES = 3;

export function WaitChip({ since, testid }) {
  const age = waitAge(since);
  if (!age || !age.level) return null;
  const tone = age.level === 'late'
    ? 'bg-red-100 text-red-700 border-red-200'
    : 'bg-amber-100 text-amber-800 border-amber-300';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${tone}`}
          title={`Nothing has moved on this transfer for ${age.days} ${age.days === 1 ? 'day' : 'days'}`}
          data-testid={testid}>
      <Hourglass size={11} /> {age.label}
    </span>
  );
}

export function WhoseMoveLine({ move, testid }) {
  if (!move) return null;
  return (
    <div className={`mx-5 mb-2 flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${move.mine
      ? 'border-amber-200 bg-amber-50 text-amber-800'
      : 'border-slate-200 bg-slate-50 text-slate-600'}`}
         data-testid={testid}>
      <Clock size={14} className={`mt-0.5 shrink-0 ${move.mine ? 'text-amber-600' : 'text-slate-400'}`} />
      <span>{move.mine && <span className="font-semibold">Your move: </span>}{move.text}</span>
    </div>
  );
}

export function TransferContents({ order, testid }) {
  // Once it has arrived, the card counts what ARRIVED, not what was sent.
  const arrived = order.status === 'received';
  const items = (order.items || []).map(i => ({
    ...i, qty: arrived && i.qty_received != null ? i.qty_received : i.qty,
  }));
  const s = unitSummary(items);
  if (!s.lines) return null;
  const shown = s.categories.slice(0, MAX_CATEGORIES);
  const hidden = s.categories.length - shown.length;
  const names = items.filter(i => (parseFloat(i.qty) || 0) > 0).map(i => i.product_name).filter(Boolean);
  const days = doorToDoorDays(order);
  return (
    <div className="px-5 pb-3" data-testid={testid}>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          {arrived ? 'Received' : 'Contents'}
        </span>
        <span className="text-sm font-semibold text-slate-800" data-testid={testid && `${testid}-totals`}>
          {s.totals.map(t => t.text).join('  |  ')}
        </span>
        <span className="text-xs text-slate-400">· {s.lines} {s.lines === 1 ? 'item' : 'items'}</span>
        {days != null && (
          <span className="text-xs text-slate-400">· took {days === 0 ? 'under a day' : `${days} ${days === 1 ? 'day' : 'days'}`} door to door</span>
        )}
      </div>
      {s.categories.length > 1 && (
        <div className="mt-1 space-y-0.5">
          {shown.map(c => (
            <div key={c.name} className="flex items-baseline gap-2 text-xs">
              <span className="w-32 shrink-0 truncate font-medium text-slate-600" title={c.name}>{c.name}</span>
              <span className="text-slate-500">{c.units.map(u => u.text).join(', ')}</span>
            </div>
          ))}
          {hidden > 0 && (
            <div className="text-xs text-slate-400">+ {hidden} more {hidden === 1 ? 'category' : 'categories'}</div>
          )}
        </div>
      )}
      {s.categories.length === 1 && (
        <div className="mt-0.5 text-xs font-medium text-slate-600">{s.categories[0].name}</div>
      )}
      <div className="mt-1 truncate text-xs text-slate-400" title={names.join(' · ')}>
        {names.slice(0, 3).join(' · ')}{names.length > 3 ? ` · + ${names.length - 3} more` : ''}
      </div>
    </div>
  );
}

const STEP_TONE = {
  done:    { bar: 'bg-emerald-500', label: 'text-slate-800', meta: 'text-slate-500' },
  now:     { bar: 'bg-amber-500',   label: 'text-amber-800', meta: 'text-amber-700' },
  problem: { bar: 'bg-red-500',     label: 'text-red-700',   meta: 'text-red-600' },
  todo:    { bar: 'bg-slate-200',   label: 'text-slate-400', meta: 'text-slate-300' },
};

export function TransferTimeline({ steps, testid }) {
  if (!steps?.length) return null;
  return (
    <ol className="grid grid-cols-2 gap-x-2 gap-y-3 border-t border-slate-100 px-5 py-3 sm:flex sm:gap-2"
        data-testid={testid}>
      {steps.map(st => {
        const tone = STEP_TONE[st.state] || STEP_TONE.todo;
        return (
          <li key={st.key} className="min-w-0 sm:flex-1" data-testid={testid && `${testid}-${st.key}`} data-state={st.state}>
            <div className={`h-1 rounded-full ${tone.bar}`} />
            <div className={`mt-1.5 flex items-center gap-1 text-xs font-semibold ${tone.label}`}>
              {st.state === 'problem' && <AlertTriangle size={11} className="shrink-0" />}
              <span className="truncate">{st.label}</span>
            </div>
            <div className={`truncate text-[11px] leading-tight ${tone.meta}`}>
              {st.at ? formatDateTimeShort(st.at) : (st.state === 'now' ? (st.note || 'Waiting') : '—')}
            </div>
            {(st.by || (st.at && st.note)) && (
              <div className={`truncate text-[11px] leading-tight ${tone.meta}`} title={st.by}>
                {st.by}{st.by && st.at && st.note ? ' · ' : ''}{st.at && st.note ? st.note : ''}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
